import { useEffect, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { PublicMenuStartPage } from "./PublicMenuStartPage"
import { useReloadMemory } from "../../hooks/useReloadMemory"
import { companyMemory } from "../../Objects/Memory/Memory"
import { getPublicMenuTheme } from "../Helpers/PublicMenuTheme"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { KeyRound, LoaderCircle, ShieldCheck, Tablet } from "lucide-react"
import { TableSelfServiceSession, usePublicTableSelfService } from "../../hooks/Tables/usePublicTableSelfService"

const TableSelfServicePage = () => {
  const { restaurantSlug = "" } = useParams<{ restaurantSlug: string }>()

  const [searchParams] = useSearchParams()

  const { t } = useTranslation("tableSelfService")

  const { handleReloadPublicMemory } = useReloadMemory()

  const { handleActivateTablet, handleValidateStoredSession } = usePublicTableSelfService()

  const activationCodeFromUrl = searchParams.get("activationCode") ?? ""

  const [activationCode, setActivationCode] = useState(
    /^\d{6}$/.test(activationCodeFromUrl) ? activationCodeFromUrl : "",
  )

  const [session, setSession] = useState<TableSelfServiceSession | null>(null)

  const [loading, setLoading] = useState(true)

  const [activating, setActivating] = useState(false)

  const [isCatalogLoading, setIsCatalogLoading] = useState(true)

  const publicMenuTheme = getPublicMenuTheme(companyMemory)

  const restaurantLogo = companyMemory.iconUrl?.trim() ? companyMemory.iconUrl.toClientRequest() : ""

  useEffect(() => {
    let active = true

    const initialize = async () => {
      const hasRestaurant = await handleReloadPublicMemory(restaurantSlug, () => {
        if (active) {
          setIsCatalogLoading(false)
        }
      })

      if (hasRestaurant) {
        const storedSession = await handleValidateStoredSession(restaurantSlug)

        if (active) {
          setSession(storedSession)
        }
      }

      if (active) {
        setLoading(false)
      }
    }

    void initialize()

    return () => {
      active = false
    }
  }, [restaurantSlug])

  const activate = async () => {
    if (activationCode.length !== 6 || activating) return

    setActivating(true)

    const activatedSession = await handleActivateTablet(restaurantSlug, activationCode)

    if (activatedSession) {
      setSession(activatedSession)
    }

    setActivating(false)
  }

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-white p-4">
        <div className="text-center">
          <img src="/logo600.png" alt={t("master_logo_alt")} className="mx-auto h-24 w-auto object-contain" />

          <LoaderCircle className="mx-auto mt-5 animate-spin text-orange-600" size={28} />
        </div>
      </main>
    )
  }

  if (session) {
    return <PublicMenuStartPage tableSelfService={session} isCatalogLoading={isCatalogLoading} />
  }

  return (
    <main className="min-h-screen bg-white">
      <div className="mx-auto flex min-h-max max-w-6xl items-center px-4 py-8 sm:px-6 sm:py-12">
        <section className="grid w-full overflow-hidden rounded-lg border bg-white shadow-2xl shadow-slate-200/70 md:grid-cols-[0.9fr_1.1fr]">
          <Animate type={AnimationTypes.slideLeftIn} duration="0.8s">
            <div
              className="flex flex-col items-center justify-center px-5 py-8 text-center md:items-start md:px-10 md:py-12 md:text-left"
              style={{
                background: publicMenuTheme.useGradientHeader
                  ? `linear-gradient(145deg, ${publicMenuTheme.secondaryColor}, ${publicMenuTheme.surfaceColor})`
                  : publicMenuTheme.secondaryColor,
                color: publicMenuTheme.textColor,
              }}
            >
              {restaurantLogo ? (
                <img
                  src={restaurantLogo}
                  alt={t("restaurant_logo_alt", { restaurant: companyMemory.companyName })}
                  className="h-24 w-24 rounded-2xl border-4 border-white bg-white object-contain shadow-lg sm:h-28 sm:w-28"
                />
              ) : (
                <div
                  className="flex h-24 w-24 items-center justify-center rounded-2xl border-4 border-white shadow-lg sm:h-28 sm:w-28"
                  style={{ backgroundColor: publicMenuTheme.primaryColor, color: publicMenuTheme.buttonTextColor }}
                >
                  <Tablet size={42} />
                </div>
              )}

              <p
                className="mt-6 text-[11px] font-black uppercase tracking-[0.22em]"
                style={{ color: publicMenuTheme.primaryColor }}
              >
                {t("title")}
              </p>
              <h1 className="mt-2 text-2xl font-black tracking-tight sm:text-3xl">
                {companyMemory.companyName || t("restaurant_fallback")}
              </h1>
              <p className="mt-3 max-w-md text-center text-sm font-medium leading-relaxed opacity-80 md:text-left">
                {t("restaurant_welcome")}
              </p>

              <div className="mt-7 flex max-w-md items-start gap-3 rounded-xl border border-white/70 bg-white/70 p-4 text-left shadow-sm">
                <ShieldCheck size={20} className="mt-0.5 shrink-0" style={{ color: publicMenuTheme.accentColor }} />
                <p className="text-xs font-semibold leading-relaxed">{t("security_hint")}</p>
              </div>
            </div>
          </Animate>

          <Animate type={AnimationTypes.slideRightIn} duration="0.8s">
            <div className="flex flex-col justify-center px-5 py-8 text-center sm:px-10 sm:py-12 md:text-left">
              <div
                className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl md:mx-0"
                style={{ backgroundColor: publicMenuTheme.secondaryColor, color: publicMenuTheme.primaryColor }}
              >
                <KeyRound size={24} />
              </div>

              <p
                className="mt-5 text-[11px] font-black uppercase tracking-[0.22em]"
                style={{ color: publicMenuTheme.primaryColor }}
              >
                {t("activation_step")}
              </p>
              <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">
                {t("activation_title")}
              </h2>
              <p className="mx-auto mt-3 max-w-lg text-center text-sm font-medium leading-relaxed text-slate-600 md:mx-0 md:text-left">
                {t("activation_description")}
              </p>

              <label className="mt-7 block text-center text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 md:text-left">
                {t("activation_code_label")}
              </label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={6}
                value={activationCode}
                onChange={(event) => setActivationCode(event.target.value.replace(/\D/g, ""))}
                onKeyDown={(event) => {
                  if (event.key === "Enter") void activate()
                }}
                placeholder={t("activation_placeholder")}
                className="mt-2 w-full rounded-2xl border-2 bg-white px-4 py-5 text-center text-3xl font-black tracking-[0.35em] text-slate-900 outline-none transition-all focus:shadow-lg"
                style={{
                  borderColor: activationCode.length === 6 ? publicMenuTheme.primaryColor : publicMenuTheme.borderColor,
                }}
                data-interaction-description-key="interactionDescriptions:publicMenu.pages.tableSelfServicePage.input.activationPlaceholder"
              />

              <button
                type="button"
                disabled={activationCode.length !== 6 || activating}
                onClick={() => void activate()}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-4 text-xs font-black uppercase tracking-[0.2em] shadow-lg transition-all hover:brightness-95 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40"
                style={{
                  backgroundColor: publicMenuTheme.buttonColor,
                  color: publicMenuTheme.buttonTextColor,
                }}
                data-interaction-description-key="interactionDescriptions:publicMenu.pages.tableSelfServicePage.button.voidActivate"
              >
                {activating && <LoaderCircle size={18} className="animate-spin" />}
                {activating ? t("activating") : t("activate")}
              </button>

              <p className="mt-5 text-center text-xs font-medium leading-relaxed text-slate-500 md:text-left">
                {t("activation_help")}
              </p>
            </div>
          </Animate>
        </section>
      </div>
    </main>
  )
}

export default TableSelfServicePage
